/**
 * Your character's journal: what it knows of the room's world (`JournalView`). The factions it came across and
 * where it stands with each (a tier, never points), the world's people it met and how they feel about it, the
 * faction quests it took on this floor, the threads the story leaves open, and a vague sense of the god's favour. Opened from the top bar (or J), in town
 * and below; the panel's HTML is built from the journal alone, so it holds still between views (`setHtml`).
 */
import type { JournalView, PlayerView } from '../shared/view.ts';
import { store } from './store.ts';
import { esc, setHtml } from './ui.ts';

/** The journal's panel: one section each, an empty one saying so. */
export function journalHtml(j: JournalView): string {
  const none = (text: string) => `<p class="muted">${esc(text)}</p>`;
  const factions = j.factions.length
    ? `<ul class="journal-list">${j.factions.map((f) => `<li><b>${esc(f.name)}</b> <span class="journal-tier tier-${esc(f.tier.toLowerCase())}">${esc(f.tier)}</span>${f.ethos ? `<span class="muted">${esc(f.ethos)}</span>` : ''}${f.lore?.length ? `<ul class="journal-lore">${f.lore.map((e) => `<li><b>${esc(e.title)}</b> ${esc(e.text)}</li>`).join('')}</ul>` : ''}</li>`).join('')}</ul>`
    : none('You have not come across any of the factions below.');
  const people = j.people.length
    ? `<ul class="journal-list">${j.people.map((p) => {
      const about = [p.what, p.faction ? `of ${p.faction}` : ''].filter(Boolean).join(' ');
      const how = [p.feeling, p.fate].filter(Boolean).join('; ');
      return `<li><b>${esc(p.name)}</b> <span class="muted">${esc(about)}</span>${how ? `<span class="journal-note">${esc(how)}</span>` : ''}</li>`;
    }).join('')}</ul>`
    : none('Nobody the dungeon remembers has crossed your path yet.');
  // This floor's faction quests you took: open, done, or failed (struck through).
  const mark = { taken: '◇', done: '✓', failed: '✗' } as const;
  const quests = j.quests.length
    ? `<ul class="journal-list">${j.quests.map((q) => `<li class="quest ${q.status}">${mark[q.status]} ${esc(q.text)}</li>`).join('')}</ul>`
    : none('No errands on this floor.');
  const threads = j.threads.length
    ? `<ul class="journal-list">${j.threads.map((t) => `<li>${esc(t.text)}</li>`).join('')}</ul>`
    : none('No loose ends that you know of.');
  const god = j.god
    ? `<h2>${esc(j.god.name)}</h2><p>${esc(j.god.favour)}</p>${j.god.owed.length ? `<p class="muted">It asked of you:</p><ul class="journal-list">${j.god.owed.map((o) => `<li>${esc(o)}</li>`).join('')}</ul>` : ''}`
    : '';
  return `
    <div class="modal-box journal-box" role="dialog" aria-label="Journal">
      <h1>${esc(j.name)}'s Journal</h1>
      <h2>Factions</h2>${factions}
      <h2>People</h2>${people}
      <h2>Quests</h2>${quests}
      <h2>Open Threads</h2>${threads}
      ${god}
      <div class="modal-actions"><button type="button" data-journal-close>Close</button></div>
    </div>`;
}

/** The top bar's Journal button (only with a journal) and the journal itself while it is open. */
export function renderJournal(view: PlayerView): void {
  const button = document.getElementById('journal-toggle');
  button?.classList.toggle('hidden', !view.journal);
  const el = document.getElementById('journal')!;
  if (!store.journalOpen || !view.journal) {
    store.journalOpen = false;
    el.classList.add('hidden');
    return setHtml(el, '');
  }
  el.classList.remove('hidden');
  setHtml(el, journalHtml(view.journal));
}
